import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  ExpandLess,
  ExpandMore,
  Storage as StorageIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { isAxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  apiService,
  ConnectorConfig,
  Project,
  ProjectCreateRequest,
  ProjectSummary,
  Schema,
} from '../services/api';
import { getApiErrorMessage } from '../services/apiErrors';
import GuidDisplay from './common/GuidDisplay';
import CreateProjectDialog, { ConnectorChoice } from './CreateProjectDialog';
import ScheduleManager from './schedule/ScheduleManager';
import { useProjectContext } from '../context/ProjectContext';

const formatDate = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const prettyPrint = (value: unknown) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const SOFT_DELETE_AFFECTED = [
  'projects — tombstoned (is_deleted, deleted_at, deleted_by, status)',
  'pipeline_schedules — scheduled runs disabled',
] as const;

const HARD_DELETE_AFFECTED = [
  'projects — row permanently removed',
  'pipeline_schedules — disabled',
  'staging_pipelines, agent_sessions — deleted',
  'operation_ledger, staging_outputs — storage files removed',
  'All project batches and batch artefacts (metadata, recipes, runs, profiles, audit logs, etc.)',
  'Batch source file storage',
  'connector_configs, schema_registry — unlinked from project (not deleted)',
] as const;

const statusOptions = ['active', 'draft', 'on_hold', 'archived'] as const;

const statusLabels: Record<string, string> = {
  active: 'Active',
  draft: 'Draft',
  on_hold: 'On Hold',
  archived: 'Archived',
  deleted: 'Deleted',
};

const projectStatusChipColor = (status: string | undefined, isDeleted: boolean): 'success' | 'error' | 'default' => {
  if (isDeleted || status === 'deleted') return 'error';
  if (status === 'active') return 'success';
  return 'default';
};

const projectStatusLabel = (status: string | undefined, isDeleted: boolean): string => {
  if (isDeleted || status === 'deleted') return statusLabels.deleted;
  return statusLabels[status ?? ''] ?? status ?? 'unknown';
};

const PROJECT_NAME_MAX_LENGTH = 255;

type SnackbarState = { severity: 'success' | 'error'; message: string } | null;

function isIgnorableHardDeleteError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status === 404) return true;
  if (status !== 409) return false;
  const detail = String(error.response?.data?.detail ?? '').toLowerCase();
  return (
    detail.includes('already deleted') ||
    detail.includes('not found') ||
    detail.includes('deleted')
  );
}

const defaultProjectForm: ProjectCreateRequest = {
  tenantId: '',
  name: '',
  description: '',
  status: 'active',
  targetSchemaId: undefined,
  metadata: {},
  createdBy: 'web-ui',
};

const blinkAnimation = {
  animation: 'pageFlash 450ms ease-in-out',
  '@keyframes pageFlash': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.3 },
    '100%': { opacity: 1 },
  },
};

const ProjectManager: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setSelectedProjectId } = useProjectContext();

  const [tenantFilter, setTenantFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [detailSummary, setDetailSummary] = useState<ProjectSummary | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState<{ open: boolean; project?: Project | null }>({
    open: false,
    project: null,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<{ open: boolean; project?: Project | null; mode?: 'soft' | 'hard' }>({
    open: false,
    project: null,
    mode: 'soft',
  });
  const [snackbar, setSnackbar] = useState<SnackbarState>(null);
  const [createFormError, setCreateFormError] = useState<string | null>(null);

  const [createForm, setCreateForm] = useState(defaultProjectForm);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    status: 'active',
    targetSchemaId: '' as string | null | undefined,
    owner: '',
    tags: '',
    sourceConnectorId: '' as string | null | undefined,
    destinationConnectorId: '' as string | null | undefined,
    metadata: {} as Record<string, any> | undefined,
  });

  const projectListParams = useMemo(
    () => ({
      tenantId: tenantFilter.trim() || undefined,
      status: statusFilter && statusFilter !== 'all' ? statusFilter : undefined,
      search: searchTerm.trim() || undefined,
      includeDeleted: true,
      limit: 250,
    }),
    [tenantFilter, statusFilter, searchTerm]
  );

  const projectsQuery = useQuery(
    ['projects-management', projectListParams],
    async () => {
      const response = await apiService.listProjectSummaries({
        tenantId: projectListParams.tenantId,
        status: projectListParams.status,
        search: projectListParams.search,
        includeDeleted: projectListParams.includeDeleted,
        limit: projectListParams.limit,
      });
      return response.projects;
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const schemaSetOptionsQuery = useQuery(
    ['schema-options'],
    () => apiService.listSchemas({}),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Fetch connectors for edit dialog source/dest dropdowns
  const connectorsQuery = useQuery<ConnectorConfig[]>(
    ['connectors', 'project-edit'],
    () => apiService.listConnectors(),
    { staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false }
  );
  const allConnectors = connectorsQuery.data ?? [];
  const sourceConnectors = allConnectors.filter((c) => c.direction === 'source');
  const destinationConnectors = allConnectors.filter((c) => c.direction === 'destination');

  const invalidateProjectQueries = () => {
    queryClient.invalidateQueries(['projects-management']);
    queryClient.invalidateQueries(['projects']);
    queryClient.invalidateQueries(['project-summary']);
  };

  const createProjectMutation = useMutation(
    async (formData: {
      name: string;
      description: string;
      status: string;
      targetSchemaId?: string;
      tags?: string;
      sourceChoice?: ConnectorChoice;
      destChoice?: ConnectorChoice;
    }) => {
      const parsedTags = formData.tags
        ? formData.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0)
        : [];

      const metadata: Record<string, any> = {};
      if (parsedTags.length > 0) {
        metadata.tags = parsedTags;
      }

      // Step 1: Create the project (with existing connector IDs if applicable)
      const project = await apiService.createProject({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        status: formData.status.trim() || undefined,
        targetSchemaId: formData.targetSchemaId || undefined,
        sourceConnectorId:
          formData.sourceChoice?.kind === 'existing' ? formData.sourceChoice.connectorId : undefined,
        destinationConnectorId:
          formData.destChoice?.kind === 'existing' ? formData.destChoice.connectorId : undefined,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        createdBy: 'web-ui',
      });

      // Step 2: Create & link new source connector if needed
      if (formData.sourceChoice?.kind === 'new') {
        const saved = await apiService.createConnector({
          ...formData.sourceChoice.pendingData,
          project_id: project.project_id,
        });
        await apiService.updateProject(project.project_id, {
          sourceConnectorId: saved.connector_id,
        });
      }

      // Step 3: Create & link new destination connector if needed
      if (formData.destChoice?.kind === 'new') {
        const saved = await apiService.createConnector({
          ...formData.destChoice.pendingData,
          project_id: project.project_id,
        });
        await apiService.updateProject(project.project_id, {
          destinationConnectorId: saved.connector_id,
        });
      }

      return project;
    },
    {
      onSuccess: (newProject) => {
        invalidateProjectQueries();
        // Ensure the global project list immediately includes the new project
        queryClient.setQueryData<Project[]>(['projects'], (existing = []) => {
          const filtered = existing.filter((p) => p.project_id !== newProject.project_id);
          return [...filtered, newProject].sort((a, b) => a.name.localeCompare(b.name));
        });
        // Switch header project selector to the new project
        setSelectedProjectId(newProject.project_id);
        setSnackbar({ severity: 'success', message: 'Project created successfully.' });
        setCreateDialogOpen(false);
        setCreateFormError(null);
        navigate('/');
        // Open the upload dialog on the dashboard for immediate transformation
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('open-new-transformation'));
        }, 500);
      },
      onError: (error) => {
        const message = getApiErrorMessage(error) || 'Failed to create project.';
        setCreateFormError(message);
      },
    }
  );

  const updateProjectMutation = useMutation(
    (projectId: string) => {
      // Build metadata with owner/tags
      const metadata: Record<string, any> = { ...(editForm.metadata ?? {}) };
      if (editForm.owner?.trim()) {
        metadata.owner = editForm.owner.trim();
      } else {
        delete metadata.owner;
      }
      const parsedTags = editForm.tags
        ? editForm.tags.split(',').map((t) => t.trim()).filter((t) => t.length > 0)
        : [];
      if (parsedTags.length > 0) {
        metadata.tags = parsedTags;
      } else {
        delete metadata.tags;
      }

      return apiService.updateProject(projectId, {
        name: editForm.name.trim() || undefined,
        description: editForm.description?.trim() || undefined,
        status: editForm.status?.trim() || undefined,
        targetSchemaId: editForm.targetSchemaId || undefined,
        sourceConnectorId: editForm.sourceConnectorId || null,
        destinationConnectorId: editForm.destinationConnectorId || null,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
      });
    },
    {
      onSuccess: () => {
        invalidateProjectQueries();
        setSnackbar({ severity: 'success', message: 'Project updated.' });
        setEditDialogOpen({ open: false, project: null });
      },
      onError: (error) => {
        const message = getApiErrorMessage(error) || 'Failed to update project.';
        setSnackbar({ severity: 'error', message });
      },
    }
  );

  const softDeleteProjectMutation = useMutation(
    (projectId: string) => apiService.softDeleteProject(projectId),
    {
      onSuccess: () => {
        invalidateProjectQueries();
        setSnackbar({ severity: 'success', message: 'Project deleted. It will be permanently removed after 30 days unless you delete it now.' });
        setDeleteDialogOpen({ open: false, project: null, mode: 'soft' });
      },
      onError: (error) => {
        const message = getApiErrorMessage(error) || 'Failed to soft delete project.';
        setSnackbar({ severity: 'error', message });
      },
    }
  );

  const hardDeleteProjectMutation = useMutation((projectId: string) => apiService.deleteProject(projectId), {
    onMutate: async (projectId) => {
      await queryClient.cancelQueries(['projects-management']);
      const queryKey = ['projects-management', projectListParams];
      const previousData = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!Array.isArray(old)) return old;
        return old.filter((s: any) => s.project?.project_id !== projectId);
      });
      return { previousData };
    },
    onSuccess: () => {
      setSnackbar({ severity: 'success', message: 'Project permanently deleted.' });
      setDeleteDialogOpen({ open: false, project: null, mode: 'hard' });
    },
    onError: (error, _projectId, context) => {
      if (isIgnorableHardDeleteError(error)) {
        invalidateProjectQueries();
        setSnackbar({ severity: 'success', message: 'Project was already removed.' });
        setDeleteDialogOpen({ open: false, project: null, mode: 'hard' });
        return;
      }
      if (context?.previousData) {
        queryClient.setQueryData(['projects-management', projectListParams], context.previousData);
      }
      const message = getApiErrorMessage(error) || 'Failed to hard delete project.';
      setSnackbar({ severity: 'error', message });
    },
  });

  const restoreProjectMutation = useMutation((projectId: string) => apiService.restoreProject(projectId), {
    onSuccess: () => {
      invalidateProjectQueries();
      setSnackbar({ severity: 'success', message: 'Project restored.' });
    },
    onError: (error) => {
      const message = getApiErrorMessage(error) || 'Failed to restore project.';
      setSnackbar({ severity: 'error', message });
    },
  });

  const schemaSetOptions: Schema[] = schemaSetOptionsQuery.data ?? [];

  const summaries = useMemo<ProjectSummary[]>(
    () =>
      (projectsQuery.data ?? []).map((summary) => ({
        ...summary,
        batches: summary.batches?.slice(0, 5) ?? [],
        recent_schemas: summary.recent_schemas?.slice(0, 5) ?? [],
      })),
    [projectsQuery.data]
  );

  const editNameConflict = useMemo(() => {
    const nameLower = editForm.name.trim().toLowerCase();
    const editingProject = editDialogOpen.project;
    if (!nameLower || !editingProject) {
      return undefined;
    }
    return summaries.find(
      (summary) =>
        summary.project.project_id !== editingProject.project_id &&
        summary.project.name.trim().toLowerCase() === nameLower
    );
  }, [editForm.name, summaries, editDialogOpen.project]);
  const isEditNameDuplicate = Boolean(editNameConflict);
  const isEditNameSoftDeletedDuplicate = Boolean(editNameConflict?.project.is_deleted);

  const existingTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const s of summaries) {
      const meta = (s.project?.metadata ?? {}) as Record<string, unknown>;
      if (Array.isArray(meta.tags)) {
        for (const t of meta.tags) {
          if (typeof t === 'string' && t.trim()) tagSet.add(t.trim());
        }
      }
    }
    return Array.from(tagSet).sort();
  }, [summaries]);

  const [expandedProjectIds, setExpandedProjectIds] = useState<string[]>([]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [blinkKey, setBlinkKey] = useState(0);

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setBlinkKey((k) => k + 1);

    try {
      await projectsQuery.refetch();
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 450);
    }
  };

  const toggleExpanded = (projectId: string) => {
    setExpandedProjectIds((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
    );
  };

  const handleOpenCreate = () => {
    setCreateForm(defaultProjectForm);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (project: Project) => {
    const meta = project.metadata ?? {};
    const tagsArr: string[] = Array.isArray(meta.tags) ? meta.tags : [];
    setEditForm({
      name: project.name,
      description: project.description ?? '',
      status: project.status ?? 'active',
      targetSchemaId: project.target_schema_id ?? '',
      owner: meta.owner ?? '',
      tags: tagsArr.join(', '),
      sourceConnectorId: project.source_connector_id ?? '',
      destinationConnectorId: project.destination_connector_id ?? '',
      metadata: project.metadata,
    });
    setEditDialogOpen({ open: true, project });
  };

  const handleOpenDelete = (project: Project, mode: 'soft' | 'hard') => {
    setDeleteDialogOpen({ open: true, project, mode });
  };

  const getSchemaSetLabel = (schemaId?: string | null) => {
    if (!schemaId) {
      return '—';
    }
    const match = schemaSetOptions.find((option) => option.schema_id === schemaId);
    return match ? `${match.schema_name} (v${match.version})` : schemaId.substring(0, 12) + '…';
  };

  return (
    <Box
      key={blinkKey}
      sx={{
        ...(isRefreshing ? blinkAnimation : {}),
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <BusinessIcon color="primary" />
        <Typography variant="h4">Projects</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={handleRefresh}
              disabled={projectsQuery.isFetching || isRefreshing}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Button variant="outlined" startIcon={<StorageIcon />} onClick={() => navigate('/schema-sets')}>
          Manage Schema Sets
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          New Project
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Search by name"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Status"
              select
              fullWidth
              size="small"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <MenuItem value="all">
                <em>All statuses</em>
              </MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {statusLabels[status] ?? status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {projectsQuery.isLoading ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1">Loading projects…</Typography>
        </Paper>
      ) : projectsQuery.isError ? (
        <Alert severity="error">
          Unable to load projects.{' '}
          {projectsQuery.error instanceof Error ? projectsQuery.error.message : 'Please try again later.'}
        </Alert>
      ) : summaries.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No projects found for the current filters.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Schema Set</TableCell>
                <TableCell>Updated / Deleted</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summaries.map((summary) => {
                const { project, schema, batches, recent_schemas } = summary;
                const isDeleted = Boolean(project.is_deleted);
                const isExpanded = expandedProjectIds.includes(project.project_id);
                return (
                  <React.Fragment key={project.project_id}>
                    <TableRow hover sx={isDeleted ? { opacity: 0.85, backgroundColor: 'action.hover' } : undefined}>
                      <TableCell>
                        <Stack spacing={0.3}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={600}>
                              {project.name}
                            </Typography>
                          </Stack>
                          <GuidDisplay guid={project.project_id} abbreviate />
                          {isDeleted && (
                            <Typography variant="caption" color="text.secondary">
                              Deleted {formatDate(project.deleted_at)} by {project.deleted_by ?? 'unknown'}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={projectStatusLabel(project.status, isDeleted)}
                          size="small"
                          color={projectStatusChipColor(project.status, isDeleted)}
                        />
                      </TableCell>
                      <TableCell>{getSchemaSetLabel(project.target_schema_id ?? schema?.schema_id)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="caption" color="text.secondary">
                            {isDeleted ? 'Deleted' : 'Updated'}
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(isDeleted ? project.deleted_at : project.updated_at)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {!isDeleted && (
                            <Tooltip title={isExpanded ? 'Hide related data' : 'View related data'}>
                              <span>
                                <IconButton size="small" onClick={() => toggleExpanded(project.project_id)}>
                                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title={isDeleted ? 'Project is deleted' : 'View details'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (!isDeleted) {
                                    setDetailSummary(summary);
                                  }
                                }}
                                disabled={isDeleted}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {!isDeleted && (
                            <>
                              <Tooltip title="Edit project">
                                <span>
                                  <IconButton size="small" onClick={() => handleOpenEdit(project)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Delete project">
                                <span>
                                  <IconButton size="small" color="error" onClick={() => handleOpenDelete(project, 'soft')}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                          {isDeleted && (
                            <Tooltip title="Restore project">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => restoreProjectMutation.mutate(project.project_id)}
                                  disabled={restoreProjectMutation.isLoading}
                                >
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {isDeleted && (
                            <Tooltip title="Permanently delete project">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleOpenDelete(project, 'hard')}
                                  disabled={
                                    restoreProjectMutation.isLoading &&
                                    restoreProjectMutation.variables === project.project_id
                                  }
                                >
                                  <DeleteForeverIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {!isDeleted && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0, borderBottom: 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 3, py: 2, backgroundColor: 'background.default', borderTop: '1px solid', borderColor: 'divider' }}>
                            <Stack spacing={2}>
                              <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="subtitle1">Recent Batches</Typography>
                                  <Chip size="small" label={batches.length} />
                                </Stack>
                                {batches.length > 0 ? (
                                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Batch ID</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell>Rows</TableCell>
                                          <TableCell>Created</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {batches.map((batch) => (
                                          <TableRow key={`${project.project_id}-batch-${batch.batch_id}`}>
                                            <TableCell>
                                              <GuidDisplay guid={batch.batch_id} abbreviate />
                                            </TableCell>
                                            <TableCell>{batch.status}</TableCell>
                                            <TableCell>{batch.total_rows ?? '—'}</TableCell>
                                            <TableCell>{formatDate(batch.created_at)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    No recent batches for this project.
                                  </Typography>
                                )}
                              </Box>

                              <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="subtitle1">Recent Schemas</Typography>
                                  <Chip size="small" label={recent_schemas.length} />
                                </Stack>
                                {recent_schemas.length > 0 ? (
                                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Draft ID</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell>Source</TableCell>
                                          <TableCell>Created</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {recent_schemas.map((draft) => (
                                          <TableRow key={`${project.project_id}-draft-${draft.schema_id}`}>
                                            <TableCell>
                                              <GuidDisplay guid={draft.schema_id} abbreviate />
                                            </TableCell>
                                            <TableCell>{draft.status}</TableCell>
                                            <TableCell>{draft.source_type}</TableCell>
                                            <TableCell>{formatDate(draft.created_at)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    No recent schemas for this project.
                                  </Typography>
                                )}
                              </Box>

                              {/* Schedules & Incremental State */}
                              <Box>
                                <ScheduleManager projectId={project.project_id} />
                              </Box>
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Project detail dialog */}
      <Dialog open={Boolean(detailSummary)} onClose={() => setDetailSummary(null)} fullWidth maxWidth="md">
        <DialogTitle>Project Details</DialogTitle>
        <DialogContent dividers>
          {detailSummary && (() => {
            const dp = detailSummary.project;
            const ss = detailSummary.schema;
            const dBatches = detailSummary.batches ?? [];
            const dDrafts = detailSummary.recent_schemas ?? [];
            const tags: string[] = Array.isArray(dp.metadata?.tags) ? dp.metadata!.tags : [];
            const isDeleted = Boolean(dp.is_deleted);
            const srcConn = dp.source_connector_id ? allConnectors.find((c) => c.connector_id === dp.source_connector_id) : null;
            const destConn = dp.destination_connector_id ? allConnectors.find((c) => c.connector_id === dp.destination_connector_id) : null;

            return (
              <Stack spacing={2.5}>
                {/* Header info */}
                <Box>
                  <Typography variant="h6" fontWeight={700}>{dp.name}</Typography>
                  <GuidDisplay guid={dp.project_id} />
                  {isDeleted && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                      Deleted {formatDate(dp.deleted_at)} by {dp.deleted_by ?? 'unknown'}
                    </Typography>
                  )}
                </Box>

                {dp.description && (
                  <Typography variant="body2" color="text.secondary">{dp.description}</Typography>
                )}

                {/* Status / dates row */}
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box mt={0.5}>
                      <Chip
                        label={projectStatusLabel(dp.status, isDeleted)}
                        size="small"
                        color={projectStatusChipColor(dp.status, isDeleted)}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body2">{formatDate(dp.created_at)}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      {isDeleted ? 'Deleted' : 'Updated'}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(isDeleted ? dp.deleted_at : dp.updated_at)}
                    </Typography>
                  </Grid>
                  {tags.length > 0 && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Tags</Typography>
                      <Stack direction="row" spacing={0.5} mt={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {tags.map((tag) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </Grid>
                  )}
                </Grid>

                {/* Connectors */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Connectors</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Source</Typography>
                      {srcConn ? (
                        <Stack spacing={0.5} mt={0.5}>
                          <Typography variant="body2" fontWeight={500}>{srcConn.name}</Typography>
                          <Chip label={srcConn.connector_type} size="small" variant="outlined" />
                        </Stack>
                      ) : dp.source_connector_id ? (
                        <Box mt={0.5}><GuidDisplay guid={dp.source_connector_id} abbreviate /></Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled" mt={0.5}>Not configured</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Destination</Typography>
                      {destConn ? (
                        <Stack spacing={0.5} mt={0.5}>
                          <Typography variant="body2" fontWeight={500}>{destConn.name}</Typography>
                          <Chip label={destConn.connector_type} size="small" variant="outlined" />
                        </Stack>
                      ) : dp.destination_connector_id ? (
                        <Box mt={0.5}><GuidDisplay guid={dp.destination_connector_id} abbreviate /></Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled" mt={0.5}>Not configured</Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>

                {/* Target Schema Set */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Target Format</Typography>
                  {ss ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" fontWeight={500}>{ss.schema_name}</Typography>
                      <Chip label={`v${ss.version}`} size="small" variant="outlined" />
                      <Chip label={ss.status} size="small" color={ss.status === 'active' ? 'success' : 'default'} />
                      <Typography variant="caption" color="text.secondary">
                        {Object.keys(ss.definition?.tables || {}).length} table(s)
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.disabled">Not configured</Typography>
                  )}
                </Paper>

                {/* Recent Batches */}
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Typography variant="subtitle2">Transformations</Typography>
                    <Chip size="small" label={dBatches.length} />
                  </Stack>
                  {dBatches.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Batch ID</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Rows</TableCell>
                            <TableCell>Created</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dBatches.map((batch) => (
                            <TableRow key={batch.batch_id} hover>
                              <TableCell><GuidDisplay guid={batch.batch_id} abbreviate /></TableCell>
                              <TableCell><Chip label={batch.status} size="small" /></TableCell>
                              <TableCell align="right">
                                {(batch.processed_rows ?? 0).toLocaleString()} / {(batch.total_rows ?? 0).toLocaleString()}
                              </TableCell>
                              <TableCell>{formatDate(batch.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No transformations yet.</Typography>
                  )}
                </Box>

                {/* Recent Schema Drafts */}
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Typography variant="subtitle2">Schemas</Typography>
                    <Chip size="small" label={dDrafts.length} />
                  </Stack>
                  {dDrafts.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Created</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dDrafts.map((draft) => {
                            const draftName = draft.schema_name || draft.metadata?.schema_name || 'Untitled Schema';
                            return (
                              <TableRow key={draft.schema_id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>{draftName}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    <GuidDisplay guid={draft.schema_id} abbreviate />
                                  </Typography>
                                </TableCell>
                                <TableCell><Chip label={draft.status} size="small" /></TableCell>
                                <TableCell>{draft.source_type}</TableCell>
                                <TableCell>{formatDate(draft.created_at)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No schemas yet.</Typography>
                  )}
                </Box>

                {/* Metadata (collapsed) */}
                {dp.metadata && Object.keys(dp.metadata).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Metadata</Typography>
                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
                      <pre style={{ margin: 0, fontSize: 12, overflow: 'auto', maxHeight: 200 }}>{prettyPrint(dp.metadata)}</pre>
                    </Paper>
                  </Box>
                )}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailSummary(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create project dialog */}
      <CreateProjectDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setCreateFormError(null);
        }}
        onSubmit={(formData) => createProjectMutation.mutate(formData)}
        isSubmitting={createProjectMutation.isLoading}
        error={createFormError}
      />

      {/* Edit project dialog */}
      <Dialog open={editDialogOpen.open} onClose={() => setEditDialogOpen({ open: false, project: null })} fullWidth maxWidth="sm">
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Name"
              fullWidth
              value={editForm.name}
              onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
              size="small"
              required
              inputProps={{ maxLength: PROJECT_NAME_MAX_LENGTH }}
              error={editForm.name.length > PROJECT_NAME_MAX_LENGTH || isEditNameDuplicate}
              helperText={
                isEditNameSoftDeletedDuplicate
                  ? 'This name belongs to a deleted project. Hard delete it to reuse the name.'
                  : isEditNameDuplicate
                    ? 'Project name already exists. Please use a unique name.'
                    : editForm.name.length > PROJECT_NAME_MAX_LENGTH
                      ? `Project name must be at most ${PROJECT_NAME_MAX_LENGTH} characters.`
                      : editForm.name.length > 0
                        ? `${editForm.name.length}/${PROJECT_NAME_MAX_LENGTH}`
                        : undefined
              }
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={editForm.description}
              onChange={(event) => setEditForm((prev) => ({ ...prev, description: event.target.value }))}
              size="small"
            />
            <TextField
              label="Status"
              select
              fullWidth
              value={editForm.status}
              onChange={(event) => setEditForm((prev) => ({ ...prev, status: event.target.value }))}
              size="small"
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {statusLabels[status] ?? status}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Schema Set"
              select
              fullWidth
              value={editForm.targetSchemaId ?? ''}
              onChange={(event) =>
                setEditForm((prev) => ({
                  ...prev,
                  targetSchemaId: event.target.value ? event.target.value : undefined,
                }))
              }
              helperText="Assign or update the schema set used for guardrails."
              size="small"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {schemaSetOptions.map((schemaSet) => (
                <MenuItem key={schemaSet.schema_id} value={schemaSet.schema_id}>
                  {schemaSet.schema_name} (v{schemaSet.version})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Owner"
              fullWidth
              value={editForm.owner}
              onChange={(event) => setEditForm((prev) => ({ ...prev, owner: event.target.value }))}
              helperText="Owner username or email"
              size="small"
            />
            <Autocomplete
              multiple
              freeSolo
              options={existingTags}
              value={
                editForm.tags
                  ? editForm.tags.split(',').map((t) => t.trim()).filter(Boolean)
                  : []
              }
              onChange={(_, newValue) =>
                setEditForm((prev) => ({
                  ...prev,
                  tags: (newValue as string[]).join(', '),
                }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option} label={option} size="small" variant="outlined" />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  size="small"
                  helperText="Select existing tags or type to create new ones"
                />
              )}
            />
            <TextField
              label="Source Connector"
              select
              fullWidth
              value={editForm.sourceConnectorId ?? ''}
              onChange={(event) =>
                setEditForm((prev) => ({
                  ...prev,
                  sourceConnectorId: event.target.value || undefined,
                }))
              }
              helperText="Select a source connector for this project"
              size="small"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {sourceConnectors.map((conn) => (
                <MenuItem key={conn.connector_id} value={conn.connector_id}>
                  {conn.name} ({conn.connector_type})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Destination Connector"
              select
              fullWidth
              value={editForm.destinationConnectorId ?? ''}
              onChange={(event) =>
                setEditForm((prev) => ({
                  ...prev,
                  destinationConnectorId: event.target.value || undefined,
                }))
              }
              helperText="Select a destination connector for this project"
              size="small"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {destinationConnectors.map((conn) => (
                <MenuItem key={conn.connector_id} value={conn.connector_id}>
                  {conn.name} ({conn.connector_type})
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen({ open: false, project: null })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => editDialogOpen.project && updateProjectMutation.mutate(editDialogOpen.project.project_id)}
            disabled={
              updateProjectMutation.isLoading ||
              !editForm.name.trim() ||
              editForm.name.length > PROJECT_NAME_MAX_LENGTH ||
              isEditNameDuplicate
            }
          >
            {updateProjectMutation.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete project dialog */}
      <Dialog
        open={deleteDialogOpen.open}
        onClose={() => setDeleteDialogOpen({ open: false, project: null, mode: 'soft' })}
        fullWidth
        maxWidth={deleteDialogOpen.mode === 'hard' ? 'sm' : 'xs'}
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        {deleteDialogOpen.mode === 'hard' ? (
          <>
            <DialogTitle
              sx={{
                color: 'error.dark',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                pb: 1.5,
                bgcolor: (theme) => theme.palette.error.main + '0A',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'error.main',
                  color: 'error.contrastText',
                  flexShrink: 0,
                }}
              >
                <WarningAmberIcon fontSize="small" />
              </Box>
              <Typography component="span" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, color: 'inherit' }}>
                Permanently Delete
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2.5 }}>
              <Alert
                severity="error"
                variant="filled"
                icon={<DeleteForeverIcon fontSize="inherit" />}
                sx={{
                  borderRadius: 1.5,
                  alignItems: 'flex-start',
                  '& .MuiAlert-message': { width: '100%' },
                  '& .MuiAlert-icon': { mt: 0.25 },
                }}
              >
                You are about to <strong>delete</strong> project{' '}
                <strong>{deleteDialogOpen.project?.name ?? 'this project'}</strong> and all associated data.
                This action cannot be undone.
              </Alert>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: 1, borderColor: 'divider' }}>
              <Button
                onClick={() => setDeleteDialogOpen({ open: false, project: null, mode: 'soft' })}
                sx={{ minWidth: 96 }}
              >
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                disableElevation
                onClick={() => {
                  if (!deleteDialogOpen.project) return;
                  hardDeleteProjectMutation.mutate(deleteDialogOpen.project.project_id);
                }}
                disabled={hardDeleteProjectMutation.isLoading}
                sx={{ minWidth: 128 }}
              >
                {hardDeleteProjectMutation.isLoading ? 'Destroying…' : 'Delete Forever'}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle
              sx={{
                color: 'warning.dark',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                pb: 1.5,
                bgcolor: (theme) => theme.palette.warning.main + '0A',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'warning.main',
                  color: 'warning.contrastText',
                  flexShrink: 0,
                }}
              >
                <DeleteIcon fontSize="small" />
              </Box>
              <Typography component="span" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, color: 'inherit' }}>
                Delete Project
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2.5 }}>
              <Alert
                severity="warning"
                variant="outlined"
                icon={<InfoIcon fontSize="inherit" />}
                sx={{
                  borderRadius: 1.5,
                  alignItems: 'flex-start',
                  bgcolor: (theme) => theme.palette.warning.main + '08',
                  '& .MuiAlert-message': { width: '100%' },
                  '& .MuiAlert-icon': { mt: 0.25 },
                }}
              >
                Project <strong>{deleteDialogOpen.project?.name ?? 'this project'}</strong> will be moved to the
                deleted list and permanently purged after 30 days unless you delete it sooner.
              </Alert>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: 1, borderColor: 'divider' }}>
              <Button
                onClick={() => setDeleteDialogOpen({ open: false, project: null, mode: 'soft' })}
                sx={{ minWidth: 96 }}
              >
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                disableElevation
                onClick={() => {
                  if (!deleteDialogOpen.project) return;
                  softDeleteProjectMutation.mutate(deleteDialogOpen.project.project_id);
                }}
                disabled={softDeleteProjectMutation.isLoading}
                sx={{ minWidth: 128 }}
              >
                {softDeleteProjectMutation.isLoading ? 'Deleting…' : 'Delete Project'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar?.message}
      />
    </Box>
  );
};

export default ProjectManager;


